import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink, TrendingUp, Sparkles, Video } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CurriculumOverview from "@/pages/CurriculumOverview";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

/* ----------------- Formatting helpers ----------------- */
function fmtCents(n) {
    if (n === null || n === undefined) return "$0.00";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n / 100);
}
// _pct fields are already percentages (10.0 = 10%). See /app/memory/auth_testing.md.
function fmtPct(v) {
    if (v === null || v === undefined) return "—";
    return `${Number(v).toFixed(2)}%`;
}
function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return iso; }
}

/* ----------------- Upgrade card (top-level) ----------------- */
function UpgradeToYearlyCard({ billing, onUpgraded }) {
    const shouldRender = billing?.plan === "monthly" && billing?.status === "active";
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!shouldRender) return;
        api.billingUpgradePreview()
            .then((p) => { setPreview(p); setPreviewErr(null); })
            .catch((e) => setPreviewErr(e.data?.detail?.code || e.data?.detail?.message || e.message));
    }, [shouldRender]);

    if (!shouldRender) return null;

    // Fallbacks when a real Stripe subscription isn't attached (e.g. dev-grant-access).
    // Static estimates keep the flagship CTA visible; the confirm modal shows a helpful
    // message if the actual upgrade call can't proceed.
    const credit_cents = preview?.monthly_credit_cents ?? 0;
    const charge_today_cents = preview?.total_due_cents ?? (preview ? (preview.new_charge_cents - preview.monthly_credit_cents) : 300000);
    const savings_cents = preview?.yearly_savings_vs_monthly_cents ?? 120000;
    const nextBilling = preview?.next_billing_date;

    async function handleConfirm() {
        setConfirming(true);
        setError(null);
        try {
            const res = await api.billingUpgrade();
            setModalOpen(false);
            onUpgraded?.(res);
        } catch (err) {
            const code = err.data?.detail?.code;
            const msg = err.data?.detail?.message || err.message;
            setError(`${code || "error"}: ${msg}`);
        } finally {
            setConfirming(false);
        }
    }

    return (
        <div
            className="rounded-lg p-6 md:p-8"
            style={{
                background: "linear-gradient(135deg, var(--st-white) 0%, #F5F7FF 100%)",
                border: "2px solid var(--st-royal)",
                boxShadow: "0 12px 40px -20px rgba(30, 72, 214, 0.35)",
            }}
            data-testid="upgrade-yearly-card"
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-lg">
                    <div className="inline-flex items-center gap-2 tracking-caps text-[11px] font-bold px-2 py-1 rounded-full"
                         style={{ background: "var(--st-royal)", color: "var(--st-white)" }}>
                        <Sparkles size={12} /> Save $1,200 / year
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-3" style={{ color: "var(--st-navy)" }}>
                        Upgrade to Yearly
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>
                        <strong>{fmtCents(charge_today_cents)}</strong> due today
                        {credit_cents > 0 && (
                            <> (with <strong>{fmtCents(credit_cents)}</strong> credited from your current month)</>
                        )}.
                        Save <strong>{fmtCents(savings_cents)}</strong> over the full year vs. paying monthly.
                    </p>
                    {previewErr && (
                        <p className="mt-3 text-xs" style={{ color: "var(--st-silver-500)" }} data-testid="upgrade-preview-note">
                            Preview shows estimated amounts — final charge is computed at confirm.
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="btn-royal text-sm"
                    data-testid="upgrade-open-modal-btn"
                >
                    Upgrade to Yearly
                </button>
            </div>

            {preview && (
                <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm" data-testid="upgrade-preview-numbers">
                    <div>
                        <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                            Credit from current month
                        </div>
                        <div className="font-serif text-xl mt-1" style={{ color: "#067647" }} data-testid="upgrade-credit">
                            {fmtCents(credit_cents)}
                        </div>
                    </div>
                    <div>
                        <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                            Charged today
                        </div>
                        <div className="font-serif text-xl mt-1" style={{ color: "var(--st-navy)" }} data-testid="upgrade-charge-today">
                            {fmtCents(charge_today_cents)}
                        </div>
                    </div>
                    <div>
                        <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                            Next billing
                        </div>
                        <div className="text-base mt-1" style={{ color: "var(--st-ink)" }} data-testid="upgrade-next-billing">
                            {fmtDate(nextBilling)}
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={modalOpen} onOpenChange={(v) => !v && setModalOpen(false)}>
                <DialogContent className="max-w-md" data-testid="upgrade-confirm-modal">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>
                            Confirm upgrade
                        </DialogTitle>
                        <DialogDescription>
                            Review the credit, total due, and next billing date, then confirm to switch from
                            monthly to the yearly plan. Enrollments are final.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 text-sm" style={{ color: "var(--st-ink)" }}>
                        <div className="grid grid-cols-2 gap-3 py-3" style={{ borderTop: "1px solid var(--st-line)", borderBottom: "1px solid var(--st-line)" }}>
                            <div>
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Credit</div>
                                <div className="font-serif text-lg mt-0.5" style={{ color: "#067647" }} data-testid="modal-credit">{fmtCents(credit_cents)}</div>
                            </div>
                            <div>
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Yearly price</div>
                                <div className="font-serif text-lg mt-0.5" style={{ color: "var(--st-navy)" }}>{fmtCents(preview?.new_charge_cents ?? 300000)}</div>
                            </div>
                            <div>
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Total due today</div>
                                <div className="font-serif text-xl mt-0.5" style={{ color: "var(--st-navy)" }} data-testid="modal-total-due">{fmtCents(charge_today_cents)}</div>
                            </div>
                            <div>
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Next billing</div>
                                <div className="text-sm mt-0.5" style={{ color: "var(--st-ink)" }}>{fmtDate(nextBilling)}</div>
                            </div>
                        </div>
                        <p>
                            You will be charged the amount above immediately. Your yearly period starts today.
                            This action cannot be reversed — <strong>enrollments are final</strong>.
                        </p>
                    </div>
                    {error && (
                        <div className="text-sm px-3 py-2 rounded"
                             style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}
                             data-testid="upgrade-error">
                            {error}
                        </div>
                    )}
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setModalOpen(false)}
                                data-testid="upgrade-cancel-btn">
                            Cancel
                        </button>
                        <button type="button" className="btn-royal text-sm" onClick={handleConfirm}
                                disabled={confirming} data-testid="upgrade-confirm-btn">
                            {confirming ? "Charging…" : "Confirm Upgrade"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function UpgradeSuccessBanner({ result, billing, onDismiss }) {
    if (!result) return null;
    const iso = result.current_period_end || billing?.current_period_end;
    return (
        <div className="rounded-lg p-4 flex gap-3 items-start"
             style={{ background: "#ECFDF3", border: "1px solid #ABEFC6" }}
             data-testid="upgrade-success-banner">
            <CheckCircle2 size={20} strokeWidth={2} color="#067647" className="mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm" style={{ color: "#067647" }}>
                <strong>Upgraded to yearly.</strong> Next billing: {fmtDate(iso)}.
            </div>
            <button type="button" onClick={onDismiss} className="text-sm underline"
                    style={{ color: "#067647" }} data-testid="upgrade-success-dismiss">
                Dismiss
            </button>
        </div>
    );
}

/* ----------------- Billing card (no longer contains upgrade card) ----------------- */
function BillingCard({ status, onManage, managing }) {
    if (!status) {
        return (
            <div className="st-surface p-6" data-testid="billing-loading">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Billing</div>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>Loading…</p>
            </div>
        );
    }
    const { plan, status: subStatus, current_period_end, in_grace_period, has_access } = status;
    const badge = (() => {
        if (subStatus === "active" && has_access) return { label: "Active", bg: "#ECFDF3", fg: "#067647", border: "#ABEFC6" };
        if (subStatus === "past_due" && in_grace_period) return { label: "Past due — grace period", bg: "#FFFAEB", fg: "#B54708", border: "#FEDF89" };
        if (subStatus === "canceled" && has_access) return { label: "Canceled — access until period end", bg: "#FFFAEB", fg: "#B54708", border: "#FEDF89" };
        if (subStatus === "canceled") return { label: "Canceled", bg: "#FEF3F2", fg: "#B42318", border: "#FECDCA" };
        if (!subStatus) return { label: "Not enrolled", bg: "var(--st-silver-100)", fg: "var(--st-ink-muted)", border: "var(--st-line)" };
        return { label: subStatus, bg: "var(--st-silver-100)", fg: "var(--st-ink-muted)", border: "var(--st-line)" };
    })();
    return (
        <div className="st-surface p-6 space-y-6" data-testid="billing-card">
            <div className="flex items-center justify-between">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Billing</div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{ background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}` }}
                      data-testid="billing-status-badge">
                    {badge.label}
                </span>
            </div>
            <div>
                <div className="font-serif text-2xl" style={{ color: "var(--st-navy)" }} data-testid="billing-plan">
                    {plan ? (plan === "monthly" ? "Monthly plan" : "Yearly plan") : "No active plan"}
                </div>
                {current_period_end && (
                    <div className="mt-1 text-sm" style={{ color: "var(--st-ink-muted)" }} data-testid="billing-period-end">
                        Current period ends: {fmtDate(current_period_end)}
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-3">
                {subStatus ? (
                    <button type="button" onClick={onManage} disabled={managing}
                            className="btn-outline-navy text-sm inline-flex items-center gap-2"
                            data-testid="billing-manage-btn">
                        {managing ? "Opening portal…" : "Manage billing"}
                        <ExternalLink size={14} strokeWidth={2} />
                    </button>
                ) : (
                    <Link to="/#enrollment" className="btn-royal text-sm" data-testid="billing-enroll-link">View plans</Link>
                )}
            </div>
        </div>
    );
}

/* ----------------- Journal summary — always numeric ----------------- */
function JournalSummaryCard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.journalStats()
            .then((s) => { setStats(s); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const total = stats?.total_entries ?? 0;
    const closed = stats?.closed_count ?? 0;
    const winRatePct = closed ? Math.round((stats.win_rate || 0) * 100) : 0;
    const totalPnl = stats?.total_pnl_absolute ?? 0;
    const avgWin = stats?.win_count ? stats.avg_win_pct : null;
    const avgLoss = stats?.loss_count ? stats.avg_loss_pct : null;

    return (
        <div className="st-surface p-6" data-testid="journal-summary-card">
            <div className="flex items-center gap-3">
                <TrendingUp size={18} strokeWidth={1.6} color="var(--st-royal)" />
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Trade Journal
                </div>
            </div>
            {loading ? (
                <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>Loading…</p>
            ) : (
                <>
                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Total entries</div>
                            <div className="font-serif text-xl mt-1" style={{ color: "var(--st-navy)" }} data-testid="journal-summary-total">
                                {total}
                            </div>
                        </div>
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Win rate</div>
                            <div className="font-serif text-xl mt-1" style={{ color: "var(--st-navy)" }} data-testid="journal-summary-win-rate">
                                {closed ? `${winRatePct}%` : "—"}
                            </div>
                        </div>
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Total P&L</div>
                            <div className="font-serif text-lg mt-1" style={{ color: totalPnl > 0 ? "#067647" : totalPnl < 0 ? "#B42318" : "var(--st-ink)" }}
                                 data-testid="journal-summary-total-pnl">
                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(totalPnl)}
                            </div>
                        </div>
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Avg win / loss</div>
                            <div className="text-sm mt-1" style={{ color: "var(--st-ink)" }}>
                                <span style={{ color: "#067647" }}>{avgWin !== null ? fmtPct(avgWin) : "—"}</span>
                                <span className="mx-1" style={{ color: "var(--st-silver-500)" }}>/</span>
                                <span style={{ color: "#B42318" }}>{avgLoss !== null ? fmtPct(avgLoss) : "—"}</span>
                            </div>
                        </div>
                    </div>
                    {total === 0 && (
                        <p className="mt-4 text-xs" style={{ color: "var(--st-silver-500)" }} data-testid="journal-summary-empty">
                            No entries yet. Log your first trade →
                        </p>
                    )}
                </>
            )}
            <Link to="/journal" className="btn-outline-navy text-sm mt-6 inline-block" data-testid="journal-view-all-cta">
                View all entries →
            </Link>
        </div>
    );
}

/* ----------------- Ancillary sections ----------------- */
function CheckoutBanner() {
    const [params, setParams] = useSearchParams();
    const state = params.get("checkout");
    if (!state) return null;
    const config = state === "success"
        ? { icon: CheckCircle2, bg: "#ECFDF3", fg: "#067647", border: "#ABEFC6",
            title: "Enrollment received.",
            body: "Your subscription is being activated. Refresh the dashboard in a moment if your plan hasn't updated yet.",
            testId: "checkout-success-banner" }
        : { icon: XCircle, bg: "#FEF3F2", fg: "#B42318", border: "#FECDCA",
            title: "Checkout cancelled.",
            body: "No charge was made. You can restart enrollment from the pricing section on the landing page.",
            testId: "checkout-cancel-banner" };
    const Icon = config.icon;
    function dismiss() {
        const next = new URLSearchParams(params);
        next.delete("checkout"); next.delete("session_id");
        setParams(next, { replace: true });
    }
    return (
        <div className="rounded-lg p-4 flex gap-3 items-start"
             style={{ background: config.bg, border: `1px solid ${config.border}` }}
             data-testid={config.testId}>
            <Icon size={20} strokeWidth={2} color={config.fg} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
                <div className="font-semibold text-sm" style={{ color: config.fg }}>{config.title}</div>
                <p className="text-sm mt-1" style={{ color: config.fg }}>{config.body}</p>
            </div>
            <button type="button" onClick={dismiss} className="text-sm underline"
                    style={{ color: config.fg }} data-testid={`${config.testId}-dismiss`}>Dismiss</button>
        </div>
    );
}

function GracePeriodBanner({ status }) {
    if (!status || !status.in_grace_period) return null;
    const daysLeft = status.grace_period_end
        ? Math.max(0, Math.ceil((new Date(status.grace_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : "?";
    return (
        <div className="rounded-lg p-4 flex gap-3 items-start"
             style={{ background: "#FFFAEB", border: "1px solid #FEDF89" }}
             data-testid="grace-period-banner">
            <AlertTriangle size={20} strokeWidth={2} color="#B54708" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm" style={{ color: "#7A2E0E" }}>
                <strong>Payment failed.</strong> Update your card in Manage billing to keep curriculum access.
                <span data-testid="grace-days-left"> {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining.</span>
            </div>
        </div>
    );
}

function LiveSessionStub() {
    const [state, setState] = useState({ loading: true, session: null, secondsUntil: null, inWindow: false });
    useEffect(() => {
        let alive = true;
        api.liveSessionsUpcoming()
            .then((d) => alive && setState({ loading: false, session: d.session, secondsUntil: d.seconds_until, inWindow: !!d.in_join_window }))
            .catch(() => alive && setState({ loading: false, session: null, secondsUntil: null, inWindow: false }));
        return () => { alive = false; };
    }, []);

    let body;
    if (state.loading) {
        body = <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>Loading…</p>;
    } else if (!state.session) {
        body = (
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>
                No sessions scheduled yet — the instructor will announce the next one shortly.
            </p>
        );
    } else {
        const when = new Date(state.session.scheduled_at).toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
        body = (
            <>
                <div className="mt-3 font-serif text-lg" style={{ color: "var(--st-navy)" }} data-testid="dash-live-title">
                    {state.session.title}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--st-ink-muted)" }} data-testid="dash-live-when">
                    {when}
                </div>
                <Link to="/live" className="mt-4 inline-block btn-outline-navy text-xs"
                      data-testid="dash-live-open-link">
                    {state.inWindow ? "Join now →" : "Open Live Sessions →"}
                </Link>
            </>
        );
    }

    return (
        <div className="st-surface p-6" data-testid="live-session-stub">
            <div className="flex items-center gap-3">
                <Video size={18} strokeWidth={1.6} color="var(--st-royal)" />
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Next Live Session
                </div>
            </div>
            {body}
        </div>
    );
}

/* ----------------- StudentDashboard root ----------------- */
export function StudentDashboard() {
    const { user, refresh } = useAuth();
    const [billing, setBilling] = useState(null);
    const [managing, setManaging] = useState(false);
    const [upgradeResult, setUpgradeResult] = useState(null);
    const [params] = useSearchParams();

    async function fetchBilling() {
        try {
            const s = await api.billingStatus();
            setBilling(s);
        } catch {
            setBilling({ plan: null, status: null, has_access: false, in_grace_period: false });
        }
    }

    useEffect(() => {
        fetchBilling();
        if (params.get("checkout") === "success") {
            const t1 = setTimeout(() => { fetchBilling(); refresh(); }, 1500);
            const t2 = setTimeout(() => { fetchBilling(); refresh(); }, 4500);
            return () => { clearTimeout(t1); clearTimeout(t2); };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function handleManage() {
        setManaging(true);
        try {
            const res = await api.billingPortal();
            window.location.href = res.url;
        } catch (err) {
            setManaging(false);
            alert(err.message || "Unable to open the billing portal.");
        }
    }

    async function handleUpgraded(result) {
        setUpgradeResult(result);
        await fetchBilling();
        await refresh();
    }

    return (
        <PageShell>
            <section className="max-w-6xl mx-auto px-6 py-14 space-y-8" data-testid="student-dashboard">
                <GracePeriodBanner status={billing} />
                <CheckoutBanner />
                <UpgradeSuccessBanner result={upgradeResult} billing={billing} onDismiss={() => setUpgradeResult(null)} />

                <div className="st-surface p-8" data-testid="student-dashboard-card">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                        Student dashboard
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }}>
                        Welcome, {user?.name || "trader"}.
                    </h1>
                    <p className="mt-4 text-base leading-relaxed max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                        Twelve structured months. No shortcuts. Progress below unlocks as you complete each phase.
                    </p>

                    {billing && !billing.has_access && (
                        <div className="mt-6 rounded-lg p-4 flex gap-3 items-start"
                             style={{ background: "#FFFAEB", border: "1px solid #FEDF89" }}
                             data-testid="student-enrollment-nudge">
                            <AlertTriangle size={20} strokeWidth={2} color="#B54708" className="mt-0.5 flex-shrink-0" />
                            <div className="text-sm" style={{ color: "#7A2E0E" }}>
                                <strong>You're signed in, but not yet enrolled.</strong> Choose a plan on the
                                landing page's <Link to="/#enrollment" className="underline">Enrollment</Link> section
                                to activate the curriculum.
                            </div>
                        </div>
                    )}
                </div>

                {/* Flagship upgrade CTA — top-level, high visibility */}
                <UpgradeToYearlyCard billing={billing} onUpgraded={handleUpgraded} />

                {billing?.has_access && <CurriculumOverview />}

                <div className="grid md:grid-cols-2 gap-6">
                    <LiveSessionStub />
                    {billing?.has_access && <JournalSummaryCard />}
                </div>

                <BillingCard status={billing} onManage={handleManage} managing={managing} />
            </section>
        </PageShell>
    );
}

export function AffiliateDashboard() {
    const { user } = useAuth();
    return (
        <PageShell>
            <section className="max-w-5xl mx-auto px-6 py-14" data-testid="affiliate-dashboard">
                <div className="st-surface p-8" data-testid="affiliate-dashboard-card">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Affiliate dashboard</div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }}>
                        Welcome, {user?.name || "partner"}.
                    </h1>
                    <p className="mt-4 text-base leading-relaxed max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                        Your affiliate dashboard — referral links, conversion tracking, and commission statements —
                        will be built in Phase 5.
                    </p>
                </div>
            </section>
        </PageShell>
    );
}

export function AdminDashboard() {
    const { user } = useAuth();
    return (
        <PageShell>
            <section className="max-w-5xl mx-auto px-6 py-14 space-y-6" data-testid="admin-dashboard">
                <div className="st-surface p-8" data-testid="admin-dashboard-card">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Admin console</div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }}>
                        Welcome, {user?.name || "admin"}.
                    </h1>
                    <p className="mt-4 text-base leading-relaxed max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                        The full admin console — curriculum CMS, user management, affiliate approvals, and platform
                        analytics — will be built out in Phase 4. In the meantime, journal review is available.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/admin/journal" className="btn-royal text-sm" data-testid="admin-open-journal-btn">
                            Review student journals
                        </Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
